/**
 * IspReportAdmin — /admin/isp-report
 *
 * Ranking de proveedores de internet (IPLocate) desde visitor_ip_intel.
 * API: GET /api/admin/isp-report
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  Globe2,
  Loader2,
  Network,
  RefreshCw,
  ShieldAlert,
  Wifi,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

type IspRow = {
  isp: string;
  asn_sample?: string | null;
  asn_name_sample?: string | null;
  company_type?: string | null;
  captures: number;
  unique_ips: number;
  vpn?: number;
  hosting?: number;
  first_seen?: string;
  last_seen?: string;
};

type AsnRow = {
  asn: string;
  asn_name?: string | null;
  isp_sample?: string | null;
  captures: number;
  unique_ips: number;
  first_seen?: string;
  last_seen?: string;
};

type RecentRow = {
  id: number;
  ip: string;
  isp: string | null;
  company: string | null;
  company_type: string | null;
  asn: string | null;
  asn_name: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  is_vpn: boolean | null;
  is_proxy: boolean | null;
  is_hosting: boolean | null;
  source_event: string;
  customer_email: string | null;
  landing_path: string | null;
  flow_name: string | null;
  created_at: string;
};

type IspReportResponse = {
  generated_at: string;
  days: number;
  brand_source_key: string;
  migration_pending?: string;
  note?: string;
  totals: {
    captures: number;
    unique_ips: number;
    unique_isps: number;
    with_isp: number;
    vpn: number;
    hosting: number;
  };
  by_isp: IspRow[];
  by_asn: AsnRow[];
  by_company_type: Array<{ company_type: string; captures: number; unique_ips: number }>;
  by_country: Array<{ country: string; captures: number; unique_ips: number }>;
  by_source_event: Array<{ source_event: string; captures: number; unique_ips: number }>;
  recent: RecentRow[];
  checkout_isp: Array<{
    isp: string;
    asn?: string | null;
    asn_name?: string | null;
    attempts: number;
    completed: number;
    emails: number;
  }>;
};

function fmtDate(s?: string | null) {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("es-AR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return s;
  }
}

export default function IspReportAdmin() {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [days, setDays] = useState("7");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<IspReportResponse | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `/api/admin/isp-report?days=${encodeURIComponent(days)}&limit=50`,
        { headers },
      );
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const json = (await res.json()) as IspReportResponse;
      setData(json);
    } catch (e) {
      toast({
        title: "No se pudo cargar el reporte ISP",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [days, getAuthHeaders, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const t = data?.totals;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <AdminPageHeader
        description="Ranking ISP/ASN (IPLocate). Lista de emails de pago → Leads recovery."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Días" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 día</SelectItem>
                <SelectItem value="7">7 días</SelectItem>
                <SelectItem value="14">14 días</SelectItem>
                <SelectItem value="30">30 días</SelectItem>
                <SelectItem value="90">90 días</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="ml-2">Actualizar</span>
            </Button>
          </div>
        }
      />

      {data?.migration_pending && (
        <Card className="border-amber-500/40">
          <CardContent className="pt-4 text-sm text-amber-200">
            Migración pendiente: <code>{data.migration_pending}</code>. Se aplica en el próximo
            deploy (<code>migrate:all</code>).
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {[
          { label: "Capturas", value: t?.captures ?? 0, icon: Wifi },
          { label: "IPs únicas", value: t?.unique_ips ?? 0, icon: Network },
          { label: "ISPs distintos", value: t?.unique_isps ?? 0, icon: Globe2 },
          { label: "Con ISP", value: t?.with_isp ?? 0, icon: Wifi },
          { label: "VPN", value: t?.vpn ?? 0, icon: ShieldAlert },
          { label: "Hosting", value: t?.hosting ?? 0, icon: ShieldAlert },
        ].map(({ label, value, icon: Icon }) => (
          <Card key={label}>
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1.5">
                <Icon className="h-3.5 w-3.5" />
                {label}
              </CardDescription>
              <CardTitle className="text-2xl tabular-nums">{loading ? "…" : value}</CardTitle>
            </CardHeader>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Por proveedor (ISP)</CardTitle>
            <CardDescription>Ranking de capturas en los últimos {days} días</CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-2 font-medium">ISP</th>
                  <th className="pb-2 pr-2 font-medium">ASN</th>
                  <th className="pb-2 pr-2 font-medium text-right">Capturas</th>
                  <th className="pb-2 font-medium text-right">IPs</th>
                </tr>
              </thead>
              <tbody>
                {(data?.by_isp || []).length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-muted-foreground">
                      Sin datos aún — se llena con leads y CTAs de conversión.
                    </td>
                  </tr>
                )}
                {(data?.by_isp || []).map((row) => (
                  <tr key={row.isp} className="border-b border-border/40">
                    <td className="py-2 pr-2">
                      <div className="font-medium">{row.isp}</div>
                      {row.company_type && (
                        <div className="text-xs text-muted-foreground">{row.company_type}</div>
                      )}
                    </td>
                    <td className="py-2 pr-2 text-xs text-muted-foreground">
                      {[row.asn_sample, row.asn_name_sample].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="py-2 pr-2 text-right tabular-nums">{row.captures}</td>
                    <td className="py-2 text-right tabular-nums">{row.unique_ips}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Por ASN</CardTitle>
            <CardDescription>Autonomous systems</CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-2 font-medium">ASN</th>
                  <th className="pb-2 pr-2 font-medium">Nombre</th>
                  <th className="pb-2 pr-2 font-medium text-right">Capturas</th>
                  <th className="pb-2 font-medium text-right">IPs</th>
                </tr>
              </thead>
              <tbody>
                {(data?.by_asn || []).map((row) => (
                  <tr key={row.asn} className="border-b border-border/40">
                    <td className="py-2 pr-2 font-mono text-xs">{row.asn}</td>
                    <td className="py-2 pr-2">
                      <div>{row.asn_name || "—"}</div>
                      {row.isp_sample && (
                        <div className="text-xs text-muted-foreground">{row.isp_sample}</div>
                      )}
                    </td>
                    <td className="py-2 pr-2 text-right tabular-nums">{row.captures}</td>
                    <td className="py-2 text-right tabular-nums">{row.unique_ips}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tipo de red</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(data?.by_company_type || []).map((r) => (
              <div key={r.company_type} className="flex justify-between text-sm">
                <span>{r.company_type}</span>
                <span className="tabular-nums text-muted-foreground">
                  {r.captures} · {r.unique_ips} IPs
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">País (IPLocate)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(data?.by_country || []).map((r) => (
              <div key={r.country} className="flex justify-between text-sm">
                <span className="font-mono">{r.country}</span>
                <span className="tabular-nums text-muted-foreground">
                  {r.captures} · {r.unique_ips} IPs
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Fuente de captura</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {(data?.by_source_event || []).map((r) => (
              <div key={r.source_event} className="flex justify-between gap-2 text-sm">
                <span className="truncate font-mono text-xs">{r.source_event}</span>
                <span className="shrink-0 tabular-nums text-muted-foreground">{r.captures}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {(data?.checkout_isp || []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">ISP en intentos de pago</CardTitle>
            <CardDescription>
              premium_checkout_intents ·{" "}
              <Link className="underline" to="/admin/recovery-leads">
                recovery leads
              </Link>
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-2 font-medium">ISP</th>
                  <th className="pb-2 pr-2 font-medium">ASN</th>
                  <th className="pb-2 pr-2 font-medium text-right">Intentos</th>
                  <th className="pb-2 pr-2 font-medium text-right">Paid</th>
                  <th className="pb-2 font-medium text-right">Emails</th>
                </tr>
              </thead>
              <tbody>
                {data!.checkout_isp.map((r) => (
                  <tr key={r.isp} className="border-b border-border/40">
                    <td className="py-2 pr-2">{r.isp}</td>
                    <td className="py-2 pr-2 text-xs text-muted-foreground">
                      {[r.asn, r.asn_name].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="py-2 pr-2 text-right tabular-nums">{r.attempts}</td>
                    <td className="py-2 pr-2 text-right tabular-nums">{r.completed}</td>
                    <td className="py-2 text-right tabular-nums">{r.emails}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Capturas recientes</CardTitle>
          <CardDescription>
            {data?.generated_at ? `Actualizado ${fmtDate(data.generated_at)}` : null}
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="pb-2 pr-2 font-medium">Cuándo</th>
                <th className="pb-2 pr-2 font-medium">ISP / ASN</th>
                <th className="pb-2 pr-2 font-medium">Geo</th>
                <th className="pb-2 pr-2 font-medium">Fuente</th>
                <th className="pb-2 font-medium">Flags</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recent || []).map((r) => (
                <tr key={r.id} className="border-b border-border/40 align-top">
                  <td className="py-2 pr-2 whitespace-nowrap text-xs">{fmtDate(r.created_at)}</td>
                  <td className="py-2 pr-2">
                    <div className="font-medium">{r.isp || "—"}</div>
                    <div className="text-xs text-muted-foreground">
                      {[r.asn, r.asn_name].filter(Boolean).join(" · ") || r.company || "—"}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground/80">{r.ip}</div>
                  </td>
                  <td className="py-2 pr-2 text-xs">
                    {[r.city, r.region, r.country].filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="py-2 pr-2">
                    <div className="font-mono text-[11px]">{r.source_event}</div>
                    {r.customer_email && (
                      <div className="text-xs text-muted-foreground">{r.customer_email}</div>
                    )}
                    {r.landing_path && (
                      <div className="text-xs text-muted-foreground">{r.landing_path}</div>
                    )}
                  </td>
                  <td className="py-2">
                    <div className="flex flex-wrap gap-1">
                      {r.is_vpn && (
                        <Badge variant="destructive" className="text-[10px]">
                          VPN
                        </Badge>
                      )}
                      {r.is_proxy && (
                        <Badge variant="secondary" className="text-[10px]">
                          proxy
                        </Badge>
                      )}
                      {r.is_hosting && (
                        <Badge variant="outline" className="text-[10px]">
                          hosting
                        </Badge>
                      )}
                      {r.company_type && (
                        <Badge
                          variant="outline"
                          className={cn("text-[10px]", "border-border/60")}
                        >
                          {r.company_type}
                        </Badge>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {data?.note && (
        <p className="text-xs text-muted-foreground">{data.note}</p>
      )}
    </div>
  );
}
